import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { listOrgs, type OrgRow } from '../../api/orgApi.js';
import { DataGrid } from '../../components/DataGrid.js';
import { ListShell, StatusBadge, sx } from './orgKit.js';

export function OrganizationListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listOrgs(token)
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <ListShell
      label="organization-list"
      title={t('org.organization.list.title')}
      newTo="/org/organizations/new"
      newLabel={t('org.organization.list.new')}
      error={error}
      loading={loading}
      empty={!loading && rows.length === 0}
      emptyText={t('org.organization.list.empty')}
    >
      {rows.length > 0 && (
        <DataGrid<OrgRow>
          caption="organizations"
          rows={rows}
          getRowId={(o) => o.orgId}
          rowTestId={(o) => `org-${o.orgId}`}
          searchPlaceholder={t('org.common.searchNameCode')}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (o) => o.orgCode,
              render: (o) => (
                <Link to={`/org/organizations/${o.orgId}/edit`} style={sx.link}>{o.orgCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (o) => o.orgName },
            { key: 'level', header: t('org.col.level'), value: (o) => o.hierarchyLevel, numeric: true },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (o) => o.recordStatusCode,
              render: (o) => <StatusBadge status={o.recordStatusCode} />,
            },
          ]}
        />
      )}
    </ListShell>
  );
}
